/**
 * 요청 시간 상한.
 *
 * `fetch` 와 axios 는 기본 타임아웃이 없다. 서버가 응답을 멈추면 브라우저는
 * nginx 의 proxy_read_timeout 이 끝날 때까지 — 실질적으로 무한히 — 스피너를
 * 돌린다. 모든 호출은 여기서 만든 signal 을 달고 나가서, 상한을 넘기면
 * 화면에 에러로 떨어진다.
 *
 * 값은 백엔드 FastApiClientProperties 의 프로파일 상한보다 조금 크게 잡는다.
 * 서버가 먼저 끊고 브라우저가 그 응답을 받는 것이 정상 순서이고, 브라우저가
 * 먼저 포기하면 서버 쪽 작업은 계속 돌아 원인 파악이 어려워진다.
 */

const toPositiveInt = (value, fallback) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

/**
 * 일반 호출의 backstop.
 *
 * 같은 /tran 엔드포인트 뒤에 짧은 호출과 긴 호출이 섞여 있다 - 백엔드가
 * serviceId 별로 STANDARD(30s) / LONG_RUNNING(120s) / RAG_VECTOR(300s) 를
 * 고르기 때문이다. 프론트는 어느 프로파일로 갈지 알 수 없으므로, 가장 긴
 * 프로파일보다 짧게 잡으면 정상 작업을 잘라 버린다(사용자에게는 실패로
 * 보이지만 서버 작업은 계속 돈다).
 *
 * 그래서 이 값은 "빠른 실패"가 아니라 nginx(330s) 와 같은 선의 마지막
 * 안전장치다. 실제로 먼저 끊는 쪽은 백엔드이고, 브라우저는 그 응답을 받는다.
 */
export const REQUEST_TIMEOUT_MS = toPositiveInt(
  import.meta.env.VITE_REQUEST_TIMEOUT_MS,
  330000
);

/** 업로드. 본문 전송 구간은 nginx 의 send/body 타임아웃이 따로 관리한다. */
export const LONG_REQUEST_TIMEOUT_MS = toPositiveInt(
  import.meta.env.VITE_LONG_REQUEST_TIMEOUT_MS,
  600000
);

/**
 * 지정한 시간 뒤에 abort 되는 signal 을 만든다.
 *
 * `AbortSignal.timeout` 이 없는 브라우저를 위해 AbortController 로 물러선다.
 *
 * @param {number} timeoutMs
 * @returns {{ signal: AbortSignal, clear: () => void }}
 */
export const createTimeoutSignal = (timeoutMs = REQUEST_TIMEOUT_MS) => {
  if (typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function") {
    return { signal: AbortSignal.timeout(timeoutMs), clear: () => {} };
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return { signal: controller.signal, clear: () => clearTimeout(timer) };
};

/**
 * fetch 옵션에 타임아웃 signal 을 붙인다. 호출부가 이미 signal 을 넘겼으면
 * 그 쪽을 존중한다(예: 사용자가 직접 취소하는 채팅 스트림).
 *
 * @param {Object} settings - fetch 옵션
 * @param {number} [timeoutMs]
 * @returns {{ settings: Object, clear: () => void }}
 */
export const withTimeout = (settings = {}, timeoutMs = REQUEST_TIMEOUT_MS) => {
  if (settings.signal) {
    return { settings, clear: () => {} };
  }
  const { signal, clear } = createTimeoutSignal(timeoutMs);
  return { settings: { ...settings, signal }, clear };
};

/**
 * 타임아웃으로 끊긴 요청인지 판별. 브라우저마다 AbortError / TimeoutError 로
 * 갈리므로 둘 다 본다.
 *
 * @param {unknown} error
 * @returns {boolean}
 */
export const isTimeoutError = (error) =>
  !!error &&
  (error.name === "TimeoutError" ||
    error.name === "AbortError" ||
    error.code === "ECONNABORTED");

/** 사용자에게 보여줄 타임아웃 문구. */
export const TIMEOUT_MESSAGE =
  "서버 응답이 제한 시간을 넘었습니다. 잠시 후 다시 시도해 주세요.";
